/**
 * validateResponse — Zod-based RTK Query transformResponse helper.
 *
 * Applies a consistent hard/soft validation policy at every API boundary:
 *   - Development: throws on schema mismatch so contract breaks surface
 *     immediately in the browser console and React error boundary.
 *   - Production: logs the mismatch to App Insights for monitoring and
 *     passes the raw value through so the UI degrades instead of crashing.
 *
 * Usage in an RTK Query slice:
 *   transformResponse: validateResponse(TargetTotalRowSchema.array())
 */
import type { ZodType } from 'zod';
import { appInsights } from '../AppInsights';

export function validateResponse<T>(schema: ZodType<T>) {
    return (raw: unknown): T => {
        const result = schema.safeParse(raw);
        if (result.success) return result.data;

        if (import.meta.env.DEV) {
            // Hard-throw in dev: surfaces to the React error boundary and
            // browser console immediately. Makes contract breaks impossible to miss.
            throw result.error;
        }

        // Soft in prod: log to App Insights, pass raw through.
        appInsights.trackException({ exception: result.error });
        return raw as T;
    };
}
